import { useState, useEffect, useRef } from "react";
import { faviconUrl } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { AdSlot } from "./AdSlot";
import { Wifi, WifiOff, Play, SkipForward } from "lucide-react";

export const VideoPlayer = ({ hosts, onHostView, poster, preroll, ads, fill = false }) => {
  const { t } = useI18n();
  const [active, setActive] = useState(0);
  const [started, setStarted] = useState(false);
  const [prerollActive, setPrerollActive] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [overlayAd, setOverlayAd] = useState(null); // { html, seconds }
  const [overlayCountdown, setOverlayCountdown] = useState(0);
  const timerRef = useRef(null);
  const repeatRef = useRef(null);
  const postrollRef = useRef(null);
  const ocRef = useRef(null);
  const repeatCountRef = useRef(0);

  const prerollEnabled = !!(preroll?.enabled && preroll?.html);
  const prerollSeconds = Math.max(0, parseInt(preroll?.seconds, 10) || 0);

  useEffect(() => {
    setActive(0);
    setStarted(false);
    setPrerollActive(false);
    setOverlayAd(null);
    clearInterval(timerRef.current);
    clearInterval(repeatRef.current);
    clearTimeout(postrollRef.current);
    clearInterval(ocRef.current);
  }, [hosts]);

  useEffect(() => () => {
    clearInterval(timerRef.current); clearInterval(repeatRef.current);
    clearTimeout(postrollRef.current); clearInterval(ocRef.current);
  }, []);

  // Recurring pre-roll + timed post-roll overlays (shown on top of the running stream).
  useEffect(() => {
    if (!started || !ads) return;
    const repMin = Math.max(0, parseInt(ads.repeatMinutes, 10) || 0);
    const postMin = Math.max(0, parseInt(ads.postrollMinutes, 10) || 0);
    const repMax = Math.max(0, parseInt(ads.repeatMax, 10) || 0); // 0 = unlimited
    repeatCountRef.current = 0;
    if (ads.repeatEnabled && ads.repeatHtml && repMin > 0) {
      repeatRef.current = setInterval(() => {
        if (repMax > 0 && repeatCountRef.current >= repMax) { clearInterval(repeatRef.current); return; }
        setOverlayAd((cur) => {
          if (cur) return cur; // don't stack
          repeatCountRef.current += 1;
          if (repMax > 0 && repeatCountRef.current >= repMax) clearInterval(repeatRef.current);
          return { html: ads.repeatHtml, seconds: Math.max(0, parseInt(ads.repeatSeconds, 10) || 0) };
        });
      }, repMin * 60000);
    }
    if (ads.postrollEnabled && ads.postrollHtml && postMin > 0) {
      postrollRef.current = setTimeout(() => {
        setOverlayAd((cur) => cur || { html: ads.postrollHtml, seconds: Math.max(0, parseInt(ads.postrollSeconds, 10) || 0) });
      }, postMin * 60000);
    }
    return () => { clearInterval(repeatRef.current); clearTimeout(postrollRef.current); };
  }, [started, ads]);

  useEffect(() => {
    if (!overlayAd) { clearInterval(ocRef.current); return; }
    setOverlayCountdown(overlayAd.seconds);
    clearInterval(ocRef.current);
    ocRef.current = setInterval(() => {
      setOverlayCountdown((c) => { if (c <= 1) { clearInterval(ocRef.current); return 0; } return c - 1; });
    }, 1000);
    return () => clearInterval(ocRef.current);
  }, [overlayAd]);

  if (!hosts || hosts.length === 0) {
    return (
      <div className="aspect-video w-full flex items-center justify-center bg-black border border-border rounded-lg text-muted-foreground">
        {t("player.noSources")}
      </div>
    );
  }

  const current = hosts[active];

  const select = (i) => {
    setActive(i);
    if (started && onHostView && hosts[i]) onHostView(hosts[i].host_id);
  };

  const startStream = () => {
    clearInterval(timerRef.current);
    setPrerollActive(false);
    setStarted(true);
    if (onHostView && current) onHostView(current.host_id);
  };

  const play = () => {
    if (prerollEnabled) {
      setPrerollActive(true);
      setCountdown(prerollSeconds);
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setCountdown((c) => {
          if (c <= 1) { clearInterval(timerRef.current); return 0; }
          return c - 1;
        });
      }, 1000);
    } else {
      startStream();
    }
  };

  return (
    <div className={fill ? "w-full h-full flex flex-col min-h-0" : "w-full"}>
      {/* Browser-style tab bar */}
      <div data-testid="player-tabbar" className="flex items-end gap-1 overflow-x-auto bg-surface rounded-t-lg border border-b-0 border-border px-2 pt-2 shrink-0">
        {hosts.map((h, i) => {
          const isActive = i === active;
          return (
            <button
              key={h.host_id}
              onClick={() => select(i)}
              data-testid={`host-tab-${h.host_name.toLowerCase()}`}
              className={`group flex items-center gap-2 px-4 py-2.5 text-sm whitespace-nowrap rounded-t-md transition-colors relative ${
                isActive
                  ? "bg-tab-active text-foreground"
                  : "bg-surface text-muted-foreground hover:text-foreground"
              }`}
            >
              {isActive && <span className="absolute top-0 left-0 right-0 h-[2px] bg-brand rounded-t-md" />}
              <img
                src={faviconUrl(h.host_domain)}
                alt=""
                width={16}
                height={16}
                className="rounded-sm"
                onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
              />
              <span>{h.host_name}</span>
              {h.status === "offline" ? (
                <span data-testid={`host-offline-badge-${h.host_name.toLowerCase()}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-offline/15 text-offline border border-offline/30">
                  <WifiOff size={11} /> {t("player.offlineTag")}
                </span>
              ) : h.status === "online" ? (
                <Wifi size={13} className="text-online" />
              ) : null}
            </button>
          );
        })}
      </div>

      {/* Player frame */}
      <div className={`relative w-full bg-black border border-border rounded-b-lg overflow-hidden ${fill ? "flex-1 min-h-0" : "aspect-video"}`}>
        {current.status === "offline" ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-6">
            <WifiOff size={40} className="text-offline" />
            <p className="text-white font-medium">{t("player.offline")}</p>
            <p className="text-sm text-zinc-400">{t("player.selectOther")}</p>
          </div>
        ) : started ? (
          <>
            <iframe
              key={current.embed_url}
              src={current.embed_url}
              title={current.host_name}
              className="w-full h-full"
              frameBorder="0"
              scrolling="no"
              allowFullScreen
              allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
              referrerPolicy="no-referrer"
            />
            {overlayAd && (
              <div className="absolute inset-0 bg-black flex items-center justify-center p-2 z-20" data-testid="recurring-ad-overlay">
                <span className="absolute top-2 left-3 z-10 text-[11px] uppercase tracking-wider text-zinc-400 font-mono pointer-events-none">
                  {t("player.adLabel")}
                </span>
                <div className="max-w-full max-h-full overflow-auto flex items-center justify-center">
                  <AdSlot html={overlayAd.html} testid="recurring-ad" className="flex items-center justify-center" />
                </div>
                <div className="absolute bottom-3 right-3 z-10">
                  {overlayCountdown > 0 ? (
                    <span data-testid="recurring-countdown" className="px-3 py-1.5 rounded-md bg-black/70 text-zinc-300 text-sm font-mono border border-white/10">
                      {t("player.adCountdown").replace("{s}", overlayCountdown)}
                    </span>
                  ) : (
                    <button onClick={() => setOverlayAd(null)} data-testid="recurring-skip"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand text-black font-semibold hover:bg-brand-hover transition-colors shadow-2xl">
                      <SkipForward size={16} /> {t("player.adSkip")}
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        ) : prerollActive ? (
          /* Pre-roll ad overlay: shown after click, before the stream iframe loads */
          <div className="absolute inset-0 bg-black flex items-center justify-center p-2" data-testid="preroll-overlay">
            <span className="absolute top-2 left-3 z-10 text-[11px] uppercase tracking-wider text-zinc-400 font-mono pointer-events-none">
              {t("player.adLabel")}
            </span>
            <div className="max-w-full max-h-full overflow-auto flex items-center justify-center">
              <AdSlot html={preroll.html} testid="preroll-ad" className="flex items-center justify-center" />
            </div>
            <div className="absolute bottom-3 right-3 z-10">
              {countdown > 0 ? (
                <span data-testid="preroll-countdown" className="px-3 py-1.5 rounded-md bg-black/70 text-zinc-300 text-sm font-mono border border-white/10">
                  {t("player.adCountdown").replace("{s}", countdown)}
                </span>
              ) : (
                <button onClick={startStream} data-testid="preroll-skip"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand text-black font-semibold hover:bg-brand-hover transition-colors shadow-2xl">
                  <SkipForward size={16} /> {t("player.adSkip")}
                </button>
              )}
            </div>
          </div>
        ) : (
          /* Click-to-play poster: the host iframe (and its ads/pop-ups) only loads on click */
          <button
            onClick={play}
            data-testid="play-overlay"
            className="group absolute inset-0 w-full h-full flex items-center justify-center"
          >
            {poster && (
              <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" onError={(e) => (e.currentTarget.style.display = "none")} />
            )}
            <span className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/50" />
            <span className="relative flex flex-col items-center gap-3">
              <span className="w-20 h-20 rounded-full bg-brand/90 flex items-center justify-center shadow-2xl transition-transform duration-300 group-hover:scale-110">
                <Play size={34} className="text-black ml-1" fill="currentColor" />
              </span>
              <span className="text-white font-medium text-sm drop-shadow flex items-center gap-2">
                <img src={faviconUrl(current.host_domain)} alt="" width={16} height={16} className="rounded-sm" onError={(e) => (e.currentTarget.style.display = "none")} />
                {t("player.clickToPlay")}
              </span>
            </span>
          </button>
        )}
      </div>
    </div>
  );
};
